import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { Lease } from '../types/api';

export const leasesKeys = {
  all: ['leases'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...leasesKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...leasesKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useLeasesList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: leasesKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ leases: Lease[] }>('/leases', { ...filters });
      return res.leases;
    },
  });
}

export function useLease(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? leasesKeys.byId(projectId, id) : ['leases', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ lease: Lease }>(`/leases/${id}`);
      return res.lease;
    },
  });
}

export interface LeaseInput {
  lease_number: string;
  alt_lease_number?: string | null | undefined;
  lease_type?: string | null | undefined;
  lease_ownership?: string | null | undefined;
  fed_rights?: string | null | undefined;
  status?: string | null | undefined;
  prospect?: string | null | undefined;
  field?: string | null | undefined;
  reviewer_user_id?: string | null | undefined;

  lessor?: string | null | undefined;
  lessee?: string | null | undefined;

  county?: string | null | undefined;
  state?: string | null | undefined;
  township?: string | null | undefined;
  range?: string | null | undefined;
  section?: string | null | undefined;
  block?: string | null | undefined;

  lease_date?: string | null | undefined;
  expire_date?: string | null | undefined;
  term?: number | null | undefined;

  book?: string | null | undefined;
  page?: string | null | undefined;
  entry?: string | null | undefined;

  provided_description?: string | null | undefined;
  description?: string | null | undefined;

  gross_acres?: number | string | null | undefined;
  net_acres?: number | string | null | undefined;
  co_net_acres?: number | string | null | undefined;

  orri?: boolean | undefined;
  royalty?: boolean | undefined;
  lease_on_minerals?: boolean | undefined;
  depth_limitations?: boolean | undefined;
  depth_limitations_remark?: string | null | undefined;
  area_pugh?: boolean | undefined;
  depth_pugh?: boolean | undefined;

  remarks?: string | null | undefined;
  reviewer_notes?: string | null | undefined;
  change_remark?: string | null | undefined;
}

export function useCreateLease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LeaseInput) => api.post<{ lease: Lease }>('/leases', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: leasesKeys.all }),
  });
}

export function useUpdateLease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<LeaseInput> }) =>
      api.patch<{ lease: Lease }>(`/leases/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: leasesKeys.all }),
  });
}

export function useDeleteLease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/leases/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: leasesKeys.all }),
  });
}
